// Home — Control Center. Stato sintetico della piattaforma + accesso rapido a
// sessioni, gestione e archivio. Solo letture (GET) verso endpoint esistenti.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons';
import { navFor } from '../components/AppShell';
import { useSession, canUse, isAdmin } from '../session';
import logoRaceSense from '../img/logo-racesense-live.svg';
import './Home.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const POLL_MS = 5000;

async function getJson(path) {
  try {
    const r = await fetch(`${API_BASE}${path}`);
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

function usePlatformStatus() {
  const [state, setState] = useState({ loaded: false, race: null, training: null, devices: null, metrics: null });
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const [race, training, devices, metrics] = await Promise.all([
        getJson('/api/race/state'), getJson('/api/training/state'), getJson('/api/race/devices'), getJson('/api/metrics'),
      ]);
      if (!alive) return;
      setState({ loaded: true, race, training, devices, metrics });
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return state;
}

const flagClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'RED FLAG') return 'is-red';
  if (s === 'YELLOW FLAG') return 'is-yellow';
  if (s === 'FORMATION LAP') return 'is-formation';
  if (s === 'FINITA') return 'is-finished';
  return 'is-green';
};

export default function Home() {
  const navigate = useNavigate();
  const { loaded, race, training, devices, metrics } = usePlatformStatus();
  const session = useSession();
  const showRace = canUse('race', session);
  const showTraining = canUse('training', session);

  const raceActive = race?.type === 'race_snapshot';
  const trainingActive = !!training?.active;
  const onlineDevices = Array.isArray(devices?.devices) ? devices.devices.length : null;
  const assignedDevices = Array.isArray(devices?.devices) ? devices.devices.filter(d => d.assignedPilotId).length : 0;

  const sessionLabel = raceActive
    ? (race.eventType === 'qualifying' ? 'Qualifica' : 'Gara')
    : trainingActive ? 'Allenamento' : 'Nessuna';
  const sessionDetail = raceActive
    ? `${race.circuit?.name || race.circuit?.id || 'circuito'} · ${race.raceStatus || 'IN CORSO'}`
    : trainingActive ? `${training.circuit?.name || ''} · ${training.driversCount ?? 0} piloti` : 'In attesa di una sessione';

  const nav = navFor(session);
  const gestione = nav.find(g => g.group === 'Gestione')?.items || [];
  const archivio = nav.find(g => g.group === 'Archivio')?.items || [];
  const inst = session?.instance;

  return (
    <div className="rs-page rs-home">
      <header className="rs-page-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--red"><span className={`rs-kicker__dot ${raceActive || trainingActive ? 'rs-kicker__dot--live' : ''}`} />Control center</span>
          <h1 className="rs-page-header__title home-logo"><img src={logoRaceSense} alt="RaceSense Live" /></h1>
          <p className="rs-page-header__subtitle">Cronometraggio, telemetria e regia broadcast per gare e allenamenti.</p>
        </div>
        {/* scorciatoia solo per tornare a una sessione già in corso */}
        {((raceActive && showRace) || (trainingActive && showTraining)) && (
          <div className="rs-page-header__actions">
            {raceActive && showRace ? (
              <button className="rs-btn rs-btn--primary rs-btn--lg" onClick={() => navigate('/race')}>
                <span className="rs-badge__dot rs-badge__dot--live" /> Vai alla regia
              </button>
            ) : (
              <button className="rs-btn rs-btn--lime rs-btn--lg" onClick={() => navigate('/allenamento')}>
                <Icon.Activity /> Sessione in corso
              </button>
            )}
          </div>
        )}
      </header>

      {/* Stato piattaforma */}
      <section className="rs-grid rs-grid--4 rs-home__stats" aria-label="Stato piattaforma">
        <div className={`rs-stat ${raceActive || trainingActive ? 'rs-stat--red' : ''}`}>
          <span className="rs-stat__label">Sessione attiva</span>
          <span className="rs-stat__value rs-home__stat-session">
            {raceActive && <span className={`rs-home__flag ${flagClass(race.raceStatus)}`} />}
            {loaded ? sessionLabel : '—'}
          </span>
          <span className="rs-stat__hint rs-truncate">{loaded ? sessionDetail : 'Caricamento…'}</span>
        </div>
        <div className={`rs-stat ${onlineDevices ? 'rs-stat--lime' : ''}`}>
          <span className="rs-stat__label">Dispositivi online</span>
          <span className="rs-stat__value">{onlineDevices ?? '—'}<small>{assignedDevices ? `${assignedDevices} assegnati` : 'ultimi 15 s'}</small></span>
          <span className="rs-stat__hint">GPS 4G collegati al server</span>
        </div>
        <div className="rs-stat">
          <span className="rs-stat__label">Pacchetti GPS</span>
          <span className="rs-stat__value rs-stat__value--mono">{metrics ? metrics.gpsPacketsPerSec : '—'}<small>/s</small></span>
          <span className="rs-stat__hint">Flusso UDP in ingresso</span>
        </div>
        <div className="rs-stat">
          <span className="rs-stat__label">Client live</span>
          <span className="rs-stat__value rs-stat__value--mono">{metrics ? metrics.wsClients : '—'}<small>WS</small></span>
          <span className="rs-stat__hint">{metrics ? `Lag event loop ${metrics.eventLoopLagMs} ms · RSS ${metrics.rssMB} MB` : 'Metriche server'}</span>
        </div>
      </section>

      {/* Sessioni */}
      <section className="rs-section">
        <div className="rs-section__head">
          <h2 className="rs-section__title">Sessioni</h2>
        </div>
        <div className={`rs-grid ${showRace && showTraining ? 'rs-grid--2' : ''}`}>
          {showRace && (
          <button className={`rs-home__cta rs-home__cta--race ${raceActive ? 'is-live' : ''}`} onClick={() => navigate('/race')}>
            <span className="rs-home__cta-icon"><Icon.Play size={26} /></span>
            <span className="rs-home__cta-text">
              <span className="rs-kicker rs-kicker--red">{raceActive ? <><span className="rs-kicker__dot rs-kicker__dot--live" />Live ora</> : 'Sessione · Gara'}</span>
              <span className="rs-home__cta-title">{raceActive ? 'Regia gara' : 'Start race'}</span>
              <span className="rs-home__cta-desc">{raceActive ? `${race.drivers?.length || 0} piloti in pista · ${race.circuit?.name || ''}` : 'Configura circuito e dispositivi, avvia formation lap e classifica live.'}</span>
            </span>
            <span className="rs-home__cta-arrow"><Icon.ArrowRight size={20} /></span>
          </button>
          )}
          {showTraining && (
          <button className={`rs-home__cta rs-home__cta--training ${trainingActive ? 'is-live' : ''}`} onClick={() => navigate('/allenamento')}>
            <span className="rs-home__cta-icon"><Icon.Activity size={26} /></span>
            <span className="rs-home__cta-text">
              <span className="rs-kicker rs-kicker--lime">{trainingActive ? <><span className="rs-kicker__dot rs-kicker__dot--lime rs-kicker__dot--live" />In corso</> : 'Sessione · Allenamento'}</span>
              <span className="rs-home__cta-title">Allenamento</span>
              <span className="rs-home__cta-desc">{trainingActive ? `${training.driversCount ?? 0} piloti · ${training.circuit?.name || ''}` : 'Sessione di test con telemetria completa, confronto giri e link da condividere.'}</span>
            </span>
            <span className="rs-home__cta-arrow"><Icon.ArrowRight size={20} /></span>
          </button>
          )}
        </div>
      </section>

      {/* Gestione */}
      <section className="rs-section">
        <div className="rs-section__head">
          <h2 className="rs-section__title">Gestione</h2>
        </div>
        <div className="rs-grid rs-grid--auto rs-home__nav">
          {gestione.map(it => {
            const Ic = it.icon;
            return (
              <button key={it.to} className="rs-card rs-card--interactive rs-home__navcard" onClick={() => navigate(it.to)}>
                <span className="rs-home__navcard-icon"><Ic size={18} /></span>
                <span className="rs-home__navcard-text">
                  <strong>{it.label}</strong>
                  <span>{it.desc}</span>
                </span>
                <Icon.ChevronRight size={16} className="rs-home__navcard-chev" />
              </button>
            );
          })}
        </div>
      </section>

      {/* Archivio */}
      {archivio.length > 0 && (
      <section className="rs-section">
        <div className="rs-section__head">
          <h2 className="rs-section__title">Archivio</h2>
        </div>
        <div className={`rs-grid ${archivio.length > 1 ? 'rs-grid--2' : ''} rs-home__nav`}>
          {archivio.map(it => {
            const Ic = it.icon;
            return (
              <button key={it.to} className="rs-card rs-card--interactive rs-home__navcard" onClick={() => navigate(it.to)}>
                <span className="rs-home__navcard-icon"><Ic size={18} /></span>
                <span className="rs-home__navcard-text">
                  <strong>{it.label}</strong>
                  <span>{it.desc}</span>
                </span>
                <Icon.ChevronRight size={16} className="rs-home__navcard-chev" />
              </button>
            );
          })}
        </div>
      </section>
      )}

      {isAdmin(session) && (
        <footer className="rs-home__strip">
          <span>COPIA <strong>{inst?.label || 'RaceSense'}</strong></span>
          <span>GPS <strong>UDP {inst?.udpPort || 8888}</strong></span>
          <span>OVERLAY <strong>OBS NATIVE</strong></span>
          <span>PULSE <strong>{inst?.firebase === false ? 'NON ATTIVO' : 'FIREBASE'}</strong></span>
        </footer>
      )}
    </div>
  );
}
